import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Análisis de problemas de asequibilidad",
    menuTitle: "Problemas de asequibilidad",
    text: `Como se mencionó en la introducción, para este indicador se consideran tres causantes de los problemas de asequibilidad: cuando el pago del dividendo supera el 30% de los ingresos, cuando el pago del arriendo lo hace y, finalmente, cuando una vez realizado el gasto en vivienda, el hogar queda bajo la línea de la pobreza. Sin embargo, al observar los datos en el siguiente gráfico, queda en evidencia que estas causantes suelen intersectarse.

  En todos los años de estimación, la mayor proporción de niñas y niños que tenían problemas de asequibilidad se debe a que, una vez realizado el gasto en vivienda, el hogar queda bajo la línea de la pobreza. Sin embargo, esta causante también muestra la mayor reducción desde 2013.

  La segunda razón prioritaria corresponde a la intersección entre pobreza y que el pago del arriendo supera el 30%: un 9% de las niñas y niños viven en viviendas cuyo arriendo supera un tercio del ingreso y, además, quedan bajo la línea de pobreza. Esta causante es la que más ha crecido en los últimos 10 años. Por su parte, el arriendo por sí solo ha aumentado en un 3% en el mismo periodo de tiempo.

  Finalmente, la proporción de niñas y niños con problemas de asequibilidad bajo el concepto de dividendo y pobreza también se duplicó, pero es bastante menor: representa a un 2% de las niñas y niños.`,
    link: "https://public.flourish.studio/visualisation/29238518/embed",
  },{
    id: "graph2",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `Desde el 2013 al 2024 se redujo la proporción de niñas y niños en viviendas con problemas de asequibilidad en 6 puntos porcentuales. Si bien entre el 2015 y el 2022 se mantuvo relativamente estable la proporción, en la última medición se observa una disminución de 3 puntos.`,
    link: "https://public.flourish.studio/visualisation/20936026/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `La proporción de niñas y niños viviendo en hogares con problemas de asequibilidad tuvo la misma tendencia en todos los tramos etarios: en 2024 hay una menor proporción de niñas y niños en viviendas no asequibles que en 2013.`,
    link: "https://public.flourish.studio/visualisation/20936031/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `Las niñas y los niños que nacieron fuera de Chile viven en hogares con problemas de asequibilidad en mayor proporción que aquellos que nacieron en el país. En 2024, el 29% de las niñas y los niños nacidos en Chile viven en hogares con un alto costo de vivienda, mientras que entre aquellos nacidos en otros países, la cifra alcanza el 49%. Las niñas y los niños nacidos en otros países, por lo tanto, tienden a presentar problemas de asequibilidad en mayor medida, dando cuenta de mayor vulnerabilidad económica. 

  Al observar las variaciones de este indicador desde 2013, se observa que para las niñas y los niños nacidos en otros países, a diferencia de los que nacieron en Chile,  los problemas de asequibilidad han aumentado en 17 puntos porcentuales. Si bien la proporción no ha aumentado desde 2017, tampoco ha disminuido, como sí lo ha hecho para niñas y niños nacidos en el país.`,
    link: "https://public.flourish.studio/visualisation/20936038/embed",
  },
  {
    id: "graph5",
    title: "Análisis por pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `Las niñas y los niños que se identifican con algún pueblo originario viven en hogares con problemas de asequibilidad en mayor proporción que aquellos que no se identifican. En 2024, el 35% de las niñas y niños pertenecientes a pueblos originarios reside en viviendas con un alto costo o pobreza residual, frente a un 30% de aquellos que no pertenecen a estos grupos. No obstante, al analizar la evolución de este indicador desde 2013, se observa que la población infantil indígena ha experimentado una reducción más pronunciada que sus pares. Mientras que en 2013 la proporción de niñas y niños de pueblos originarios en viviendas no asequibles alcanzaba un 47%, para 2024 esta cifra se redujo en 12 puntos porcentuales, acortando significativamente la brecha existente entre ambos grupos.`,
    link: "https://public.flourish.studio/visualisation/29237540/embed",
  },
  {
    id: "graph6",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `En 2024, un 76% de las niñas y niños del quintil I, es decir el de menores ingresos, viven en viviendas con problemas de asequibilidad. En el quintil de mayores ingresos, en contraste, esta proporción se reduce a un 3%. Si bien el quintil de menores ingresos es el que tiene la mayor proporción –con 50 puntos de diferencia con el quintil que le sigue–, es también el que ha tenido una reducción más pronunciada desde el 2013. Entre las niñas y los niños del II y el III quintil hubo un aumento significativo en la proporción que residía en viviendas no asequibles entre 2017 y 2022. En 2024, mientras que en el III quintil se redujo la proporción de niñas y niños en viviendas con problemas de asequibilidad, la proporción del II quintil se mantuvo constante.`,
    link: "https://public.flourish.studio/visualisation/29237253/embed",
  },
  {
    id: "graph7",
    title: "Análisis por situación de la vivienda",
    menuTitle: "\u2022 Situación vivienda",
    text: `A continuación se presenta la proporción de niñas y niños con problemas de asequibilidad según otras carencias habitacionales: hacinamiento, allegamiento externo e interno. Primero, se observa que entre niñas y niños en hogares hacinados (más de 2,5 personas por habitación), hay una mayor proporción de niñas y niños con altos costos de vivienda (44%) en comparación con quienes no viven en hacinamiento (29%). Segundo, entre las niñas y los niños en hogares con allegamiento externo (aquellos hogares que comparten la vivienda con otros hogares, entendiendo hogar como el grupo de personas que tiene un único presupuesto alimentario), en 2024 entre ambos grupos hay 11 puntos porcentuales de diferencia. Niñas y niños en viviendas con allegamiento externo viven en mayor proporción con problemas de asequibilidad Finalmente, en relación con el allegamiento interno —es decir, viviendas en las que conviven dos o más núcleos familiares, entendidos como grupos principales dentro del hogar compuestos por personas con vínculos de parentesco cercano o relaciones de dependencia—, en 2024 se observa una mayor proporción de problemas de asequibilidad entre las niñas y los niños que viven en hogares sin allegamiento interno (34%), en comparación con un 20% de aquellos que sí presentan esta condición.`,
    link: "https://public.flourish.studio/visualisation/20936043/embed",
  },
  {
    id: "graph8",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `Hasta el 2022, las niñas y los niños que viven en zonas urbanas tendían a vivir en menor proporción en hogares con un alto costo de la vivienda que aquellos que residen en zonas rurales. Entre ambos grupos, para el 2022, hay 4 puntos porcentuales de diferencia. En 2024, en cambio, ambas proporciones se equiparan, marcado principalmente por la reducción en la proporción de niñas y niños en viviendas no asequibles en zonas rurales.`,
    link: "https://public.flourish.studio/visualisation/20936033/embed",
  },
  {
    id: "graph9",
    title: "Análisis por región",
    menuTitle: "\u2022 Región",
    text: `Al año 2024, las regiones con mayor proporción de niñas y niños que viven en hogares con problemas de asequibilidad corresponden a La Araucanía (39%) y Coquimbo (36%). En las únicas regiones en que disminuyó de forma estadísticamente significativa la proporción entre 2022 y 2024 de niñas y niños en viviendas no asequibles fueron la Metropolitana, pasando de 33% a 29%, y la región de Ñuble, de 40% a 32%. En el resto de las regiones se mantuvo constante la proporción.`,
    link: "https://public.flourish.studio/visualisation/20936044/embed",
  }
];


const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: (graph.id === "graph1" || graph.id === "graph2") ? "20px" : "30px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;

